
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import type { WaitingMessage, MessageCategory } from "@/types/chat";

interface WaitingIndicatorProps {
  waitingMessages: WaitingMessage[];
  isShowingWaitingMessages: boolean;
  category: MessageCategory;
  className?: string;
}

export function WaitingIndicator({ 
  waitingMessages, 
  isShowingWaitingMessages,
  category,
  className = "" 
}: WaitingIndicatorProps) {
  
  // Se non ci sono messaggi, mostra un messaggio di fallback
  if (waitingMessages.length === 0) {
    const fallbackMessage = {
      id: 'fallback',
      text: 'Elaboro la tua richiesta...',
      category: category || 'GENERALE',
      delay: 0,
      priority: 1
    };
    
    return (
      <div className={`space-y-3 ${className}`} data-testid="waiting-indicator">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ 
            duration: 0.4,
            ease: "easeOut"
          }}
          className="flex items-start space-x-3 p-4 bg-gold-surface rounded-lg border-l-4 border-gold"
        >
          <Loader2 className="h-5 w-5 text-gold animate-spin mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-gold-dark font-medium">
              {fallbackMessage.text}
            </p>
            <div className="flex space-x-1 mt-2">
              <div className="w-2 h-2 bg-gold rounded-full animate-pulse" />
              <div className="w-2 h-2 bg-gold rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
              <div className="w-2 h-2 bg-gold rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`} data-testid="waiting-indicator">
      <AnimatePresence>
        {waitingMessages.map((message, index) => {
          const isLatest = index === waitingMessages.length - 1;
          
          return (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.5, // Rallentato per effetto più drammatico
                ease: "easeOut",
                delay: index * 0.3 // Aumentato da 0.1s a 0.3s per stagger più evidente
              }}
              className="flex items-start space-x-3 p-4 bg-gold-surface rounded-lg border-l-4 border-gold"
            >
              {/* Solo l'ultimo messaggio mostra l'indicatore di caricamento */}
              {isLatest && (
                <Loader2 className="h-5 w-5 text-gold animate-spin mt-0.5 shrink-0" />
              )}
              {!isLatest && (
                <div className="h-5 w-5 mt-0.5 shrink-0 flex items-center justify-center">
                  <div className="w-2 h-2 bg-gold rounded-full" />
                </div>
              )}
              
              <div className="flex-1">
                <motion.p 
                  className="text-sm text-gold-dark font-medium"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  {message.text}
                </motion.p>
                
                {/* Solo l'ultimo messaggio mostra i dots animati */}
                {isLatest && (
                  <motion.div 
                    className="flex space-x-1 mt-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.3 }}
                  >
                    <div className="w-2 h-2 bg-gold rounded-full animate-pulse" />
                    <div 
                      className="w-2 h-2 bg-gold rounded-full animate-pulse" 
                      style={{ animationDelay: '0.2s' }} 
                    />
                    <div 
                      className="w-2 h-2 bg-gold rounded-full animate-pulse" 
                      style={{ animationDelay: '0.4s' }} 
                    />
                  </motion.div>
                )}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
